import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { queryClient } from '@/queryClient.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import {
	RESOURCE_NAMES,
	resourceSchema,
	readResources,
	parseResource,
} from '@/api/api.js'




export const accountSchema = Object.freeze(Object.assign(
	{},
	{
		login: Object.freeze({
			key: 'login',
			title: 'логін',
		}),
		password: Object.freeze({
			key: 'password',
			title: 'пароль',
		}),
		name: Object.freeze({
			key: 'name',
			title: 'повне ім\'я',
		}),
		organizationId: Object.freeze({
			key: 'organization_id',
			title: '',
		}),
		organization: Object.freeze({
			key: 'organization',
			title: 'організація',
		}),
		departmentsScope: Object.freeze({
			key: 'departments_scope',
			title: '',
		}),
		sectorsScope: Object.freeze({
			key: 'sectors_scope',
			title: '',
		}),
		role: Object.freeze({
			key: 'role',
			title: 'роль',
			allowed: Object.freeze({
				admin: {
					key: 'admin',
					title: 'адміністратор',
				},
				engineer: {
					key: 'engineer',
					title: 'інженер',
				},
				manager: {
					key: 'manager',
					title: 'менеджер',
				},
				dispatcher: {
					key: 'dispatcher',
					title: 'диспетчер',
				},
				visitor: {
					key: 'visitor',
					title: 'відвідувач',
				},
			})
		}),
		phone: Object.freeze({
			key: 'phone',
			title: 'номер телефону',
		}),
		position: Object.freeze({
			key: 'position',
			title: 'посада',
		}),
		isEnabled: Object.freeze({
			key: 'isEnabled',
			title: 'ввімкнений',
		}),
		gotify: Object.freeze({
			key: 'gotify',
			title: '',
			userId: Object.freeze({
				key: 'user_id',
				title: '',
			}),
			testToken: Object.freeze({
				key: 'test_token',
				title: '',
			}),
			stationsToken: Object.freeze({
				key: 'stations_token',
				title: '',
			}),
			postsToken: Object.freeze({
				key: 'posts_token',
				title: '',
			}),
		}),
		settings: Object.freeze({
			key: 'settings',
			title: '',
			stationsColumns: Object.freeze({
				key: 'stations_columns',
				title: '',
			}),
			stationsSorting: Object.freeze({
				key: 'stations_sorting',
				title: '',
				sortBy: Object.freeze({
					key: 'sortBy',
					title: '',
				}),
				sortType: Object.freeze({
					key: 'sortType',
					title: '',
				}),
			}),
			stationsView: Object.freeze({
				key: 'stations_view',
				title: '',
			}),
			postColumns: Object.freeze({
				key: 'post_columns',
				title: '',
			}),
			eventsAcknowledgeTime: Object.freeze({
				key: 'events_acknowledge_time',
				title: '',
			}),
		}),
	},
	resourceSchema,
))

// "Date" type pasport fields (flatten object keys)
const ACCOUNT_DATE_PATHS = [
	accountSchema.updateTime.key,
	accountSchema.createTime.key,
	`${accountSchema.settings.key}.${accountSchema.settings.eventsAcknowledgeTime.key}`
]

// Parse data field from DB to Data() objects
function parseAccount(item) {
	const result = parseResource(item, ACCOUNT_DATE_PATHS)
	// Save organization object to predetermined field
	result[accountSchema.organization.key] = { ...result[accountSchema.organizationId.key] }
	result[accountSchema.organizationId.key] = result[accountSchema.organization.key][accountSchema.id.key]

	return result
}




export function useAccounts() {
	const accountStore = useAccontStore()
	const qyeryKey = [RESOURCE_NAMES.accounts, ]

	const accountsQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: qyeryKey,
		queryFn: async () => {
			const res = await readResources(
				RESOURCE_NAMES.accounts,
				accountStore.userLogin,
				accountStore.userPassword,
				[`embedded={"${accountSchema.organizationId.key}":1}`,],
			)

			return res.map((el) => parseAccount(el))
		},
		// Keep results warm to reduce repeated network requests.
		staleTime: 30 * 60 * 1000,
		gcTime: 60 * 60 * 1000,
	})

	const allAccountsMap = computed(() => new Map(
		accountsQuery.data.value?.map(account => [account[accountSchema.id.key], account])
	))

	const allAccounts = computed(() => [...allAccountsMap.value.values()] )

	// Read all used logins
	async function fetchAllLogins() {
		try {
			const accounts = await readResources(
				RESOURCE_NAMES.accounts,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			)

			return accounts.map((account) => account[accountSchema.login.key])
		} catch (error) {
			console.error('on fetch all logins', error)
			return null
		}
	}

	// Get account with filters
	function allAccountsFiltered(filters={
		organization: null,
		// departments: null,
		// sectors: null,
	}) {
		// console.log('allAccountsFiltered', filters, allAccounts.value)
		return [...allAccountsMap.value.values()].filter(
			(account) => filters.organization == null || account[accountSchema.organizationId.key] == filters.organization
		)
	}

	function getAccount(userId) {
		// console.log('getAccount', userId, allAccountsMap.value, )
		return allAccountsMap.value.get(userId)
	}

	function getUserName(userId) {
		const account = allAccountsMap.value.get(userId)
		return account ? account[accountSchema.name.key] : undefined
	}

	async function invalidateAccounts() {
		await queryClient.invalidateQueries({
			queryKey: qyeryKey,
			exact: false, // inavalidate queries that begins from this key
		})
	}

	return {
		accountSchema,

		allAccounts,
		isLoading: accountsQuery.isLoading,
		isError: accountsQuery.isError,
		error: accountsQuery.error,
		
		fetchAllLogins,
		allAccountsFiltered,
		getAccount,
		getUserName,
		invalidateAccounts,
	}
}